import { en, type TranslationKey } from '../i18n/en'
import type { ReportDatasetKey } from './reportStorage'
import type { ReportScopeV1 } from './reportScope'
import { DATASETS, getDataset, type ReportDataset } from './reportDatasets'

// v2.2 findings.md #58: dataset and column labels in reportDatasets.ts are
// English data. Screens show them through these helpers, which use the
// `reports.dataset.<key>` / `reports.column.<key>` translation keys when they
// exist and fall back to the dataset's own English label otherwise (so a new
// column without a key still renders).

type Translate = (key: TranslationKey, vars?: Record<string, string | number>) => string

const hasKey = (key: string): key is TranslationKey => Object.prototype.hasOwnProperty.call(en, key)

export const translateDatasetLabel = (t: Translate, datasetKey: string, fallback: string): string => {
  const key = `reports.dataset.${datasetKey}`
  return hasKey(key) ? t(key) : fallback
}

export const translateColumnLabel = (t: Translate, columnKey: string, fallback: string): string => {
  const key = `reports.column.${columnKey}`
  return hasKey(key) ? t(key) : fallback
}

// Optional per-column help (findings #213): `reports.columnHelp.<key>`.
export const translateColumnHelp = (t: Translate, columnKey: string): string | undefined => {
  const key = `reports.columnHelp.${columnKey}`
  return hasKey(key) ? t(key) : undefined
}

// Coded values a row carries, by the column that holds them (findings #220).
// Only exact codes translate: a free-text species stays as the office wrote it.
const CODED_COLUMNS: Record<string, string> = {
  species: 'species',
  speciesKey: 'species',
  feedConversionRatioInFlightBand: 'band',
  lowConfidence: 'yesNo',
  completed: 'yesNo',
  stage: 'stage',
  tgcSource: 'tgcSource',
  projectionStatus: 'projectionStatus'
}

const codedKeys = Object.keys(CODED_COLUMNS)

export const localizeReportRows = <T extends Record<string, unknown>>(t: Translate, rows: readonly T[]): T[] => rows.map((row) => {
  let next: Record<string, unknown> | null = null
  for (const column of codedKeys) {
    const value = row[column]
    if (typeof value !== 'string' && typeof value !== 'boolean') continue
    const key = `reports.value.${CODED_COLUMNS[column]}.${String(value)}`
    if (!hasKey(key)) continue
    next = next ?? { ...row }
    next[column] = t(key)
  }
  return (next ?? row) as T
})

export const localizeDataset = (t: Translate, dataset: ReportDataset): ReportDataset => ({
  ...dataset,
  label: translateDatasetLabel(t, dataset.key, dataset.label),
  columns: dataset.columns.map((column) => ({
    ...column,
    label: translateColumnLabel(t, column.key, column.label),
    help: translateColumnHelp(t, column.key)
  }))
})

export const getLocalizedDataset = (t: Translate, key: ReportDatasetKey): ReportDataset | undefined => {
  const dataset = getDataset(key)
  return dataset ? localizeDataset(t, dataset) : undefined
}

export const getLocalizedDatasets = (t: Translate): ReportDataset[] => DATASETS.map((dataset) => localizeDataset(t, dataset))

/**
 * One-line "what data" (filter) summary of a report's scope, e.g.
 * "1 instalación(es) · 4 lote(s) · desde 2026-01-01", or "todos los datos".
 * Counts only — the widget card has no farm/lot names at hand.
 */
export const summarizeReportScope = (t: Translate, scope: ReportScopeV1 | null | undefined): string => {
  if (!scope) return t('dashboardNew.widgets.scopeAll')
  const parts: string[] = []
  if (scope.farmIds.length > 0) parts.push(t('dashboardNew.widgets.scopeFacilities', { count: scope.farmIds.length }))
  if (scope.productionUnitIds.length > 0) parts.push(t('dashboardNew.widgets.scopeUnits', { count: scope.productionUnitIds.length }))
  const lotCount = Math.max(scope.lotIds.length, scope.lotKeys.length)
  if (lotCount > 0) parts.push(t('dashboardNew.widgets.scopeLots', { count: lotCount }))
  if (scope.fromDate) parts.push(t('dashboardNew.widgets.scopeFrom', { date: scope.fromDate }))
  if (scope.toDate) parts.push(t('dashboardNew.widgets.scopeTo', { date: scope.toDate }))
  return parts.length > 0 ? parts.join(' · ') : t('dashboardNew.widgets.scopeAll')
}
